import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from "../../plugins/axios";
import {
    Dialog,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    Select,
    MenuItem,
    Button,
    Box,
    Typography,
    IconButton,
    Grid,
    Switch,
} from '@mui/material';
import {
    Delete as DeleteIcon,
    CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import { makeStyles } from '@mui/styles';
import { TimePicker, DatePicker } from '@mui/x-date-pickers';
import { useDropzone } from "react-dropzone";
import AccountAutoComplete from '../AccountAutoComplete';

const useStyles = makeStyles((theme) => ({
    label: {
        fontSize: '0.8em',
        fontWeight: 'bold'
    },
    drop: {
        display: 'flex',
        padding: '20px',
        background: '#eee',
        borderRadius: '5px',
        margin: '10px',
        flexDirection: 'column'
    },
    safetyToggle: {
        display: 'flex',
        justifyContent: 'space-between',
        background: '#f4eeff',
        borderRadius: '5px',
        alignItems: 'center',
        paddingLeft: '12px',
    },
    adviceType: {
        border: '1px solid #444',
        padding : '0px 3px',
        background: '#ddd',
        borderRadius: '4px',
        marginRight: '5px'
    }
}));

const apiUrl = process.env.REACT_APP_WORKNEST_TOOLS_API_URL;

export default function TimesheetEntryDialog({ entry, setEntry, date, activityTypes, calcDuration, advisoryTypes, onSubmitted }) {

    const classes = useStyles();

    const [contacts, setContacts] = useState([]);

    const [submitting, setSubmitting] = useState(false);

    const [selectedDate, setSelectedDate] = useState(date);

    useEffect(() => { console.log({ entry }) }, [entry]);

    useEffect(() => {

        if (!entry.accountId)
            return;

        const fetchContacts = async () => {
            let response = await axios.get(`${apiUrl}/getContacts/${entry.accountId}`)
            setContacts(response.data);
        }

        fetchContacts();
    }, [entry.accountId]);

    const removeFile = (file) => {
        setEntry({
            ...entry,
            files: entry.files.filter((f) => f !== file),
        });
    };

    const handleAccountChange = (v) => {
        setEntry({
            ...entry,
            accountId: v && v.value,
            accountName: v && v.text,
        });
    };

    useEffect(() => {
        if (entry.accountId == null) {
            setEntry((prevEntry) => ({
                ...prevEntry,
                contracts: [],
                contractId: "",

            }));
        } else {
            (async () => {
                var result = await axios.get(`${apiUrl}/accountcontracts/${entry.accountId}`);
                let contracts = result.data;
                setEntry((prevEntry) => ({
                    ...prevEntry,
                    contracts,
                    contractId: prevEntry.contractId || (contracts.length === 1 ? contracts[0].key : ""),
                }));
            })();
        }

    }, [entry.accountId, setEntry]);

    const updateEntry = useCallback((field, value) => {
        setEntry(prev => ({
            ...prev,
            [field]: value,
        }));
    }, [setEntry]);

    const contracts = entry?.contracts || [];

    const updateTimeEntry = (field, value, date) => {

        let dt = new Date(date || selectedDate);
        dt.setHours(value.getHours());
        dt.setMinutes(value.getMinutes());
        dt.setSeconds(0);
        updateEntry(field, dt);
    };

    const setDate = (date) => {
        setSelectedDate(date);

        if (entry.startTime)
            updateTimeEntry('startTime', entry.startTime, date);

        if (entry.endTime)
            updateTimeEntry('endTime', entry.endTime, date);
    }

    const onDrop = useCallback(
        (acceptedFiles) => {
            let existing = entry.files || [];
            let newFiles = acceptedFiles.filter(
                (f) => existing.filter((e) => e.path === f.path).length === 0
            );
            setEntry((prevEntry) => ({
                ...prevEntry,
                files: [...existing, ...newFiles]
            }));            
        },
        [entry.files, setEntry]
    );

    const { getRootProps, getInputProps } = useDropzone({ onDrop });

    const submit = async () => {
        setSubmitting(true);
        try {
            var formData = new FormData();

            if (entry.files) {
                entry.files.forEach((file) => {
                    formData.append("files", file);
                });
            }

            formData.append("entryJson", JSON.stringify(entry));

            await axios.post(
                `${apiUrl}/addtimesheetentry`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            onSubmitted();
        } finally {
            setSubmitting(false);
        }
    };

    const isValid = useMemo(() => {
        if (entry.accountId && !entry.contractId)
            return false;

        if (!entry.accountId && !entry.description)
            return false;

        if (!entry.activityType)
            return false;

        let duration = calcDuration(entry.startTime, entry.endTime);        

        if (duration === "Invalid" || duration === "")
            return false;

        return true;
    }, [calcDuration, entry]);

    const handleClose = (event, reason) => {
        if (reason && reason === "backdropClick")
            return;
        setEntry();
    }

    if (!entry)
        return null;

    console.log({contacts, entry});

    return (
        <Dialog
            onClose={handleClose}
            open={!!entry}
        >
            <DialogContent>
                <Typography variant="h6">Add Entry</Typography>
                <Box display="flex" alignItems="center" gap="15px">
                    <Typography style={{ fontSize: '0.8em' }}>Submit an entry for</Typography>
                    <DatePicker
                        value={selectedDate}
                        onChange={setDate}
                        slotProps={{
                            textField: {
                                size: 'small',
                                InputProps: {
                                    style: {
                                        width: '150px'
                                    }
                                }
                            },
                            field: {
                                readOnly: true,
                            },
                        }}
                    />
                </Box>                
                <Box display="flex" gap="5px" flexDirection="column">
                    <Grid container spacing={2}>
                        <Grid item xs={7}>
                            <Typography className={classes.label}>Account</Typography>
                            {entry.id
                                ? <TextField disabled value={entry.accountName} fullWidth size="small" />
                                : <AccountAutoComplete handleAccountChange={handleAccountChange} />
                            }
                        </Grid>
                        <Grid item xs={5}>
                            <Typography className={classes.label}>Contract</Typography>
                            <FormControl style={{ width: "100%" }} disabled={!!entry.id}>
                                <Select
                                    size="small"
                                    displayEmpty
                                    value={entry.contractId || ""}
                                    onChange={(e) => updateEntry("contractId", e.target.value)}
                                >
                                    {contracts.length === 0 && entry.accountId == null &&
                                        <MenuItem value="">
                                            <em>&lt;-- choose Account</em>
                                        </MenuItem>
                                    }
                                    {contracts.length === 0 && entry.accountId &&
                                        <MenuItem value="">
                                            <em>none available</em>
                                        </MenuItem>
                                    }
                                    {contracts.length > 1 &&
                                        <MenuItem value="">
                                            <em>select contract</em>
                                        </MenuItem>
                                    }
                                    {contracts.map((c) =>
                                        <MenuItem key={c.key} value={c.key}>
                                            {c.value}
                                        </MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                    <Box display="flex" flexDirection="column">
                        <Typography className={classes.label}>Description</Typography>
                        <TextField
                            size="small"
                            fullWidth
                            multiline
                            rows={4}
                            inputProps={{ maxLength: 8192 }}
                            value={entry.description || ""}
                            onChange={(e) => updateEntry("description", e.target.value)}
                        />
                    </Box>
                    <FormControl style={{ width: "100%" }} disabled={!!entry.id}>
                        <Typography className={classes.label}>Activity Type</Typography>
                        <Select
                            displayEmpty
                            size="small"
                            value={entry.activityType || ""}
                            onChange={(e) => updateEntry("activityType", e.target.value)}
                        >
                            <MenuItem value="">
                                <em>None</em>
                            </MenuItem>
                            {activityTypes.map((t) =>
                                <MenuItem key={t} value={t}>{t}</MenuItem>
                            )}
                        </Select>
                    </FormControl>
                    {(entry.activityType === "Advisory" || entry.activityType === "Remote Advisory") && entry.accountId &&
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Typography className={classes.label}>Advisory Type</Typography>
                                <Select
                                    disabled={!!entry.id}
                                    fullWidth
                                    displayEmpty
                                    size="small"
                                    value={entry.advisoryType || ""}
                                    onChange={(e) => updateEntry("advisoryType", e.target.value)}
                                >
                                    <MenuItem value="">
                                        <em>None</em>
                                    </MenuItem>
                                    {advisoryTypes.map((t) =>
                                        <MenuItem key={t} value={t}>{t}</MenuItem>
                                    )}
                                </Select>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography className={classes.label}>Contact</Typography>
                                {entry.id ?
                                    <TextField disabled value={entry.contactName} fullWidth size="small" /> :
                                    <Select
                                        fullWidth
                                        displayEmpty
                                        size="small"
                                        value={entry.contactId || ""}
                                        onChange={(e) => updateEntry("contactId", e.target.value)}
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>                             
                                        {contacts.map(c =>
                                            <MenuItem key={c.id} value={c.id}>
                                                <Box display="flex" flexDirection="column">
                                                    <Box>{c.name}</Box>
                                                    <Box>{c.adviceTypes.map(at => <span className={classes.adviceType} key={at}>{at}</span>)}</Box>
                                                </Box>                                                
                                            </MenuItem>
                                        )}
                                    </Select>
                                }

                            </Grid>
                            <Grid item xs={12}>
                                <div className={classes.safetyToggle}>
                                    <Typography className={classes.label}>Would you like to add this advice to SafetyNest?</Typography>
                                    <Switch checked={entry.publishToSafetyNest || false} onChange={(e) => updateEntry('publishToSafetyNest', e.target.checked)} disabled={!!entry.id} />
                                </div>
                            </Grid>
                        </Grid>
                    }
                    <Grid container spacing={2}>
                        <Grid item xs={5}>
                            <Typography className={classes.label}>Start</Typography>
                            <TimePicker
                                value={entry.startTime || null}
                                onChange={(value) => updateTimeEntry("startTime", value)}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                    },
                                    field: {
                                        readOnly: true,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={5}>
                            <Typography className={classes.label}>End</Typography>
                            <TimePicker
                                value={entry.endTime || null}
                                onChange={(value) => updateTimeEntry("endTime", value)}
                                slotProps={{
                                    textField: {
                                        size: 'small',                                        
                                    },
                                    field: {
                                        readOnly: true,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <Typography className={classes.label}>Duration</Typography>
                            <Typography pl={2} pt={2}>{calcDuration(entry.startTime, entry.endTime)}</Typography>
                        </Grid>
                    </Grid>
                    <section>
                        <div className={classes.drop} {...getRootProps()}>
                            <input {...getInputProps()} />
                            <Box display="flex" alignItems="center">
                                <CloudUploadIcon style={{ marginRight: "5px" }} />
                                <Typography style={{ cursor: "default" }}>Drag and drop your files here or <strong>click</strong> to browse</Typography>
                            </Box>
                            {entry.files && (
                                <ul>
                                    {entry.files.map((file) => (
                                        <li key={file.path}>
                                            <Box alignItems="center" display="flex">
                                                <Box>{file.path} </Box>
                                                <IconButton
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        removeFile(file);
                                                    }}
                                                    size="large">
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </section>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setEntry()}>Cancel</Button>
                <Button onClick={submit} color='primary' disabled={submitting || !isValid}>Save</Button>
            </DialogActions>
        </Dialog>
    )
}